import { useState, useEffect } from "react";
import "../../styles/sidebar/ReportsPage.css";
import { useMamdani } from "../../context/MamdaniContext";

const API_BASE = "https://api.anti-bot.ai";

async function fetchPDFBlob(url, options) {
  const res = await fetch(url, options);
  if (!res.ok) throw new Error("Error loading report");
  return await res.blob();
}

function triggerDownload(blobUrl, filename) {
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export default function ReportsPage() {
  const { mamdaniMode: mm, data: mmData } = useMamdani();
  const [loading, setLoading]     = useState(null);
  const [politicians, setPoliticians] = useState([]);
  const [selectedPol, setSelectedPol] = useState("");
  const [customFrom, setCustomFrom]   = useState("");
  const [customTo, setCustomTo]       = useState("");
  const [minScore, setMinScore]       = useState(50);
  const [preview, setPreview]         = useState(null);

  useEffect(() => {
    if (mm) return;
    fetch(`${API_BASE}/api/politicians`)
      .then(r => r.json())
      .then(data => { if (data.success) setPoliticians(data.politicians); })
      .catch(console.error);
  }, [mm]);

  function closePreview() {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  async function handle(type) {
    setLoading(type);
    try {
      const url = selectedPol
        ? `${API_BASE}/reports/api/reports/${type}/pdf?politician=${selectedPol}`
        : `${API_BASE}/reports/api/reports/${type}/pdf`;
      const blob    = await fetchPDFBlob(url);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch { alert("שגיאה בטעינת הדוח"); }
    setLoading(null);
  }

  async function handleCustom() {
    if (!customFrom || !customTo) { alert("יש לבחור תאריך התחלה וסיום"); return; }
    setLoading("custom");
    try {
      const daysBack = Math.max(1, Math.round((new Date() - new Date(customFrom)) / 86400000));
      const blob = await fetchPDFBlob(`${API_BASE}/reports/api/reports/custom/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ politician: selectedPol || null, min_score: Number(minScore), days_back: daysBack }),
      });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch { alert("שגיאה בטעינת הדוח"); }
    setLoading(null);
  }

  return (
    <div className="rp-page" dir={mm ? "ltr" : "rtl"}>

      {/* ── מודל תצוגה מקדימה ── */}
      {preview && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            width: '90%', maxWidth: '900px', height: '85vh',
            background: '#1a1a1a', borderRadius: '16px',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            {/* כותרת מודל */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
              flexShrink: 0
            }}>
              <span style={{ color: '#E85D04', fontWeight: 700, fontSize: '15px' }}>
                📄 {mm ? "Preview —" : "תצוגה מקדימה —"} {preview.filename}
              </span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => triggerDownload(preview.url, preview.filename)}
                  style={{
                    background: '#E85D04', border: 'none', color: '#fff',
                    borderRadius: '8px', padding: '8px 18px', fontWeight: 700,
                    fontSize: '13px', cursor: 'pointer'
                  }}
                >⬇ {mm ? "Download PDF" : "הורד PDF"}</button>
                <button
                  onClick={closePreview}
                  style={{
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    color: '#fff', borderRadius: '8px', padding: '8px 14px',
                    fontSize: '13px', cursor: 'pointer'
                  }}
                >✕ {mm ? "Close" : "סגור"}</button>
              </div>
            </div>
            {/* iframe */}
            <iframe
              src={preview.url}
              style={{ flex: 1, border: 'none', width: '100%' }}
              title="PDF Preview"
            />
          </div>
        </div>
      )}

      <h1 className="rp-title">{mm ? "Mamdani Bot Reports" : "מערכת דוחות AntiBot"}</h1>

      {/* כרטיסי דוחות */}
      <div className="rp-cards">

        <div className="rp-card">
          <div>
            <h3>{mm ? "Daily Report" : "דוח יומי"}</h3>
            <p>{mm ? "Bot analysis by account" : "ניתוח בוטים לפי פוליטיקאים"}</p>
          </div>

          <button
            className="rp-btn"
            onClick={() => handle("daily")}
          >
            {loading === "daily" ? (mm ? "Generating..." : "מכין...") : "⬇ PDF"}
          </button>
        </div>

        <div className="rp-card">
          <div>
            <h3>{mm ? "Weekly Report" : "דוח שבועי"}</h3>
            <p>{mm ? "Last 7 days" : "7 ימים אחרונים"}</p>
          </div>

          <button
            className="rp-btn"
            onClick={() => handle("weekly")}
          >
            {loading === "weekly" ? (mm ? "Generating..." : "מכין...") : "⬇ PDF"}
          </button>
        </div>

        <div className="rp-card">
          <div>
            <h3>{mm ? "Monthly Report" : "דוח חודשי"}</h3>
            <p>{mm ? "Last 30 days" : "30 ימים אחרונים"}</p>
          </div>
          <button className="rp-btn" onClick={() => handle("monthly")}>
            {loading === "monthly" ? (mm ? "Generating..." : "מכין...") : "⬇ PDF"}
          </button>
        </div>

        {/* דוח מותאם אישית */}
        <div className="rp-card rp-card-custom">
          <div>
            <h3>{mm ? "Custom Report" : "דוח מותאם אישית"}</h3>
            <p>{mm ? "Select date range and score" : "בחר טווח תאריכים וציון"}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '14px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: '#B7A99A', display: 'block', marginBottom: '4px' }}>{mm ? "From" : "מתאריך"}</label>
                  <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                    style={{ width: '100%', background: 'rgba(74,63,55,0.25)', border: '1px solid #4A3F37', borderRadius: '8px', padding: '7px 10px', color: '#F3E7D7', fontSize: '13px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: '#B7A99A', display: 'block', marginBottom: '4px' }}>{mm ? "To" : "עד תאריך"}</label>
                  <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                    style={{ width: '100%', background: 'rgba(74,63,55,0.25)', border: '1px solid #4A3F37', borderRadius: '8px', padding: '7px 10px', color: '#F3E7D7', fontSize: '13px' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#B7A99A', display: 'block', marginBottom: '4px' }}>{mm ? `Min Bot Score: ${minScore}` : `ציון בוט מינימלי: ${minScore}`}</label>
                <input type="range" min="0" max="100" value={minScore} onChange={e => setMinScore(e.target.value)}
                  style={{ width: '100%', accentColor: '#D89B5A' }} />
              </div>
            </div>
          </div>
          <button className="rp-btn" onClick={handleCustom}>
            {loading === "custom" ? (mm ? "Generating..." : "מכין...") : "⬇ PDF"}
          </button>
        </div>

      </div>

      <h2 className="rp-subtitle">{mm ? "Watchlist Accounts" : "פוליטיקאים במערכת"}</h2>

      <div className="rp-politicians">

        <div
          className={`rp-pol-card ${selectedPol === "" ? "active" : ""}`}
          onClick={() => setSelectedPol("")}
        >
          <div>
            <div className="rp-pol-name">{mm ? "All Accounts" : "כל הפוליטיקאים"}</div>
          </div>
        </div>

        {(mm ? mmData.accounts : politicians).map(p => (
          <div
            key={p.username}
            className={`rp-pol-card ${selectedPol === p.username ? "active" : ""}`}
            onClick={() => setSelectedPol(p.username)}
          >
            <img
              src={p.profile_image_url || p.profile_image}
              alt={p.display_name || p.name || p.username}
              className="rp-avatar"
            />
            <div>
              <div className="rp-pol-name">{p.display_name || p.name || `@${p.username}`}</div>
              <div className="rp-pol-bots">
                {mm ? `${p.bots?.length ?? 0} bots` : `${p.bot_count} בוטים`}
              </div>
            </div>
          </div>
        ))}

      </div>

    </div>
  );
}